/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

const AUTH_COOKIE_NAME = 'Authorization';
const TENANT_COOKIE_NAME = 'TenantId';

function readRawCookieValue(name: string, cookieHeader: string): string {
  const pattern = new RegExp(`(?:^|;\\s*)${name}=([^;]*)`);
  const matched = cookieHeader.match(pattern);
  return matched?.[1]?.trim() ?? '';
}

function fullyDecodeCookieComponent(encoded: string): string {
  if (!encoded) {
    return '';
  }
  let value = encoded;
  for (let i = 0; i < 3; i++) {
    try {
      const next = decodeURIComponent(value);
      if (next === value) {
        break;
      }
      value = next;
    } catch (error) {
      void error;
      break;
    }
  }
  return value;
}

/**
 * 解析 Authorization Cookie 为解码后的 UC userkey（用于 Authorization 请求头）。
 * 与 tology-web authorizationCookie.ts 规则对齐。
 */
export function parseAuthorizationCookieValue(
  cookieHeader: string,
): string {
  const encoded = readRawCookieValue(AUTH_COOKIE_NAME, cookieHeader);
  if (!encoded) {
    return '';
  }

  let value = fullyDecodeCookieComponent(encoded);
  if (value && !value.endsWith('==') && encoded.includes('==') && !encoded.includes('%3D')) {
    const legacyKey = encoded.split('==')[0];
    if (legacyKey) {
      value = `${fullyDecodeCookieComponent(legacyKey)}==`;
    }
  }
  return value.trim();
}

/** 与 one-backend-web / tology-web 对齐：读取 Authorization Cookie（支持 URL 编码） */
export function readAuthorizationFromCookie(): { value: string } {
  try {
    return { value: parseAuthorizationCookieValue(document.cookie || '') };
  } catch (error) {
    void error;
    return { value: '' };
  }
}

/** 与 tology-web / abr-chat-frame 对齐：读取 TenantId Cookie */
export function readTenantIdFromCookie(): string | undefined {
  try {
    const raw = document.cookie || '';
    const value = fullyDecodeCookieComponent(
      readRawCookieValue(TENANT_COOKIE_NAME, raw),
    );
    return value || undefined;
  } catch (error) {
    void error;
    return undefined;
  }
}
